import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Calendar, Mail, Phone, MapPin } from "lucide-react";
import { Layout } from "./Layout";
import { Reveal } from "./Reveal";
import { PageHero } from "./PageHero";
import { CINEMATIC } from "../data/site";
import { useContent } from "../context/ContentContext";
import { usePageMeta } from "../hooks/usePageMeta";

const slugify = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

// Formats "YYYY-MM-DD" or ISO into DD/MM/YYYY
const fmtDate = (iso) => {
  const s = String(iso || "").slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}/${m}/${y}` : s;
};

/**
 * Shared legal/content page template. Takes a list of sections where each is
 * { title, body }. Body is a function receiving a `ctx` object (contact details)
 * so each page can reference the business email/phone from the back office.
 */
export const LegalPage = ({ eyebrow, titleLines, intro, lastUpdated, sections, metaTitle, metaDescription }) => {
  const content = useContent();
  const contact = content?.contact || {};
  const email = contact.email || "hello@elinsconsultancy.co.uk";
  const phone = contact.phone || "";
  const address = contact.address || "";

  usePageMeta(metaTitle, metaDescription);

  const resolvedSections = useMemo(
    () =>
      sections.map((s) => ({
        ...s,
        id: s.id || slugify(s.title),
        body: typeof s.body === "function" ? s.body({ email, phone, address }) : s.body,
      })),
    [sections, email, phone, address]
  );

  const tocRef = useRef(null);

  // Smooth scroll with small offset for the sticky header
  const jump = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top, behavior: "smooth" });
    window.history.replaceState(null, "", `#${id}`);
  };

  return (
    <Layout>
      <PageHero
        eyebrow={eyebrow}
        lines={titleLines}
        text={intro}
        image={CINEMATIC.archColumns}
        imageAlt="Classical fluted columns on a London period facade"
      />

      <section data-testid="legal-meta" className="bg-cream border-b border-forest/10">
        <div className="wrap-x py-5 flex flex-wrap items-center gap-x-6 gap-y-3">
          <span className="inline-flex items-center gap-2 text-[11.5px] tracking-[0.2em] uppercase font-bold text-forest/70">
            <Calendar size={13} strokeWidth={1.6} /> Last updated {fmtDate(lastUpdated)}
          </span>
          <span className="hidden sm:inline w-px h-4 bg-forest/15" />
          {email && (
            <a
              href={`mailto:${email}`}
              className="inline-flex items-center gap-2 text-[12.5px] text-forest/70 hover:text-gold-dark transition-colors"
              data-testid="legal-email-link"
            >
              <Mail size={13} strokeWidth={1.6} />
              {email}
            </a>
          )}
          {phone && (
            <a
              href={`tel:${phone.replace(/[^+\d]/g, "")}`}
              className="inline-flex items-center gap-2 text-[12.5px] text-forest/70 hover:text-gold-dark transition-colors"
            >
              <Phone size={13} strokeWidth={1.6} />
              {phone}
            </a>
          )}
          {address && (
            <span className="inline-flex items-center gap-2 text-[12.5px] text-forest/70">
              <MapPin size={13} strokeWidth={1.6} />
              {address.split("\n")[0]}
            </span>
          )}
        </div>
      </section>

      <section className="bg-cream">
        <div className="wrap-x py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-[0.9fr_2fr] gap-12 lg:gap-20 items-start">
          <aside
            ref={tocRef}
            data-testid="legal-toc"
            className="lg:sticky lg:top-24 bg-cream-3 border border-forest/10 p-6 sm:p-8"
          >
            <p className="eyebrow text-gold-dark">Contents</p>
            <nav aria-label="Page contents" className="mt-4 space-y-1">
              {resolvedSections.map((s, i) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  onClick={jump(s.id)}
                  data-testid={`legal-toc-${i + 1}`}
                  className="flex items-baseline gap-3 py-2 text-[13.5px] leading-[1.5] text-forest hover:text-gold-dark transition-colors border-l-2 border-transparent hover:border-gold pl-3 -ml-3"
                >
                  <span className="text-[10px] tracking-[0.18em] font-bold text-forest/40 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {s.title}
                </a>
              ))}
            </nav>
          </aside>

          <article data-testid="legal-body" className="prose-elin max-w-none">
            {resolvedSections.map((s, i) => (
              <Reveal key={s.id} delay={Math.min(i * 0.05, 0.2)}>
                <section
                  id={s.id}
                  data-testid={`legal-section-${i + 1}`}
                  className="pb-12 mb-12 border-b border-forest/10 last:border-b-0 scroll-mt-24"
                >
                  <h2
                    className="font-serif text-3xl sm:text-[34px] font-medium text-forest leading-[1.15] mt-0 !mb-6"
                  >
                    <span className="text-gold-dark text-[14px] font-bold tracking-[0.26em] uppercase block mb-3">
                      Section {String(i + 1).padStart(2, "0")}
                    </span>
                    {s.title}
                  </h2>
                  <div className="space-y-5 text-[14.5px] leading-[1.8] text-ink/80">{s.body}</div>
                </section>
              </Reveal>
            ))}

            <div
              data-testid="legal-footer-note"
              className="bg-forest text-cream p-7 sm:p-9 grain relative"
            >
              <p className="eyebrow text-gold-soft !mb-3">Questions?</p>
              <p className="text-[14px] leading-[1.8] text-cream/80 max-w-xl relative z-[2]">
                If any of this is unclear, or you'd like to make a specific request, send us a
                note at{" "}
                <a
                  href={`mailto:${email}`}
                  className="text-gold-soft underline underline-offset-4 hover:text-gold transition-colors"
                >
                  {email}
                </a>{" "}
                and we'll come back to you promptly.
              </p>
              <div className="mt-5 flex flex-wrap gap-3 relative z-[2]">
                <Link to="/terms-and-conditions" className="text-[11px] tracking-[0.22em] uppercase font-bold text-cream/60 hover:text-gold-soft transition-colors">Terms</Link>
                <span className="text-cream/20">·</span>
                <Link to="/privacy-policy" className="text-[11px] tracking-[0.22em] uppercase font-bold text-cream/60 hover:text-gold-soft transition-colors">Privacy</Link>
                <span className="text-cream/20">·</span>
                <Link to="/complaints-procedure" className="text-[11px] tracking-[0.22em] uppercase font-bold text-cream/60 hover:text-gold-soft transition-colors">Complaints</Link>
              </div>
            </div>
          </article>
        </div>
      </section>
    </Layout>
  );
};

export default LegalPage;
